import React, { useCallback, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { useParams } from "react-router";
import get from "lodash/get";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import useAuth from "src/hooks/useAuth";
import useRoles from "src/hooks/user/useRoles";
import useMutation from "src/hooks/useMutation";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import { UPDATE_PROJECT_MUTATION } from "src/constants/graphql/mutations";
import { getProjectVariables } from "src/utils/profile/settings";
import { getCustomerToggles } from "./customerTogglesConfig";
import UnsavedChangesDialog from "../../modals/UnsavedChanges/UnsavedChangesDialog";
import ReceivingWorkflowSelectionModal from "../../modals/ProjectActions/ReceivingWorkflowSelectionModal";
import {
  RECEIVING_WORKFLOW_CARD_OPTIONS,
  RECEIVING_WORKFLOW_RECEIVING_ASSISTANT,
  RECEIVING_WORKFLOW_ONSITE_TO_WAREHOUSE,
  RECEIVING_WORKFLOW_ONSITE_CAPTURE,
  RECEIVING_WORKFLOW_ONSITE_MANIFEST,
  getWorkflowFromValues,
  applyWorkflowToFormik,
} from "../../modals/ProjectActions/projectReceivingWorkflow";

const MODE_ICON_MAP = {
  [RECEIVING_WORKFLOW_RECEIVING_ASSISTANT]: WarehouseOutlinedIcon,
  [RECEIVING_WORKFLOW_ONSITE_TO_WAREHOUSE]: LocalShippingOutlinedIcon,
  [RECEIVING_WORKFLOW_ONSITE_CAPTURE]: CameraAltOutlinedIcon,
  [RECEIVING_WORKFLOW_ONSITE_MANIFEST]: FactCheckOutlinedIcon,
};

export default function CustomerTogglesModal({ open, onClose }) {
  const { projectId } = useParams();
  const { user } = useAuth();
  const { settings } = user.company;
  const { values, setFieldValue } = useFormikContext();
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const [saving, setSaving] = useState(false);
  const [modeModalOpen, setModeModalOpen] = useState(false);
  const [updateProject] = useMutation(UPDATE_PROJECT_MUTATION);

  const availableCardOptions = useMemo(() =>
    RECEIVING_WORKFLOW_CARD_OPTIONS.filter((o) =>
      o.value !== RECEIVING_WORKFLOW_ONSITE_MANIFEST || settings.onsite_manifest
    ),
    [settings.onsite_manifest]
  );
  const currentWorkflow = getWorkflowFromValues(values);
  const currentWorkflowOption = RECEIVING_WORKFLOW_CARD_OPTIONS.find((o) => o.value === currentWorkflow);

  const handleModeSelect = (workflow) => {
    applyWorkflowToFormik(workflow, setFieldValue);
  };

  const categories = getCustomerToggles(settings, isAdminOrManager, values.costCenters, values);
  const modeFields = [
    "storageEnabled",
    "settings.onsite_capture_to_warehouse_storage",
    "settings.onsite_manifest",
    "settings.onsite_inventory_capture",
  ];
  const collectOptionFields = (options) =>
    options.flatMap((option) =>
      option.type === "sub-group" ? collectOptionFields(option.options) : [option.field]
    );
  const toggleFields = [
    ...modeFields,
    ...categories.flatMap((category) => collectOptionFields(category.options)),
  ];

  const initialValuesRef = useRef(null);
  if (open && initialValuesRef.current === null) {
    const snapshot = {};
    for (const field of toggleFields) {
      snapshot[field] = get(values, field);
    }
    initialValuesRef.current = snapshot;
  }
  if (!open) {
    initialValuesRef.current = null;
  }

  const getFieldValue = (field) => get(values, field);

  const handleToggle = (field) => (event) => {
    const newValue = event.target.checked;
    setFieldValue(field, newValue);

    // Mutual exclusivity: referenceAssets and importedAssets cannot both be enabled
    if (newValue && field === "referenceAssets") {
      setFieldValue("importedAssets", false);
    }
    if (newValue && field === "importedAssets") {
      setFieldValue("referenceAssets", false);
    }

    if (newValue && field === "groupedAssets") {
      setFieldValue("groupedSerializedAssets", false);
    }

    if (newValue && field === "groupedSerializedAssets") {
      setFieldValue("groupedAssets", false);
    }
  };

  const handleSelectChange = (field) => (event) => {
    const newValue = event.target.value === "" ? null : event.target.value;
    setFieldValue(field, newValue);
  };

  const handleNumberChange = (field) => (event) => {
    const newValue = parseInt(event.target.value, 10) || 0;
    setFieldValue(field, newValue);
  };

  const handleMultiSelectChange = (field) => (event) => {
    const newValue = typeof event.target.value === "string"
      ? event.target.value.split(",")
      : event.target.value;
    setFieldValue(field, newValue);
  };

  const renderFieldControl = (option) => {
    if (option.type === "number") {
      return (
        <TextField
          type="number"
          value={getFieldValue(option.field) || 0}
          onChange={handleNumberChange(option.field)}
          size="small"
          inputProps={{ min: 0, style: { textAlign: "center" } }}
          sx={{
            width: 50,
            flexShrink: 0,
            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
          }}
        />
      );
    }

    if (option.type === "select") {
      return (
        <Select
          value={getFieldValue(option.field) ?? (option.allowBlank ? "" : "60")}
          onChange={handleSelectChange(option.field)}
          size="small"
          sx={{ flexShrink: 0, minWidth: 180 }}
        >
          {option.allowBlank && (
            <MenuItem value="">
              {option.blankLabel || "None"}
            </MenuItem>
          )}
          {option.choices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value}>
              {choice.label}
            </MenuItem>
          ))}
        </Select>
      );
    }

    if (option.type === "multi-select") {
      const selectedValues = getFieldValue(option.field) || [];
      const selectedLabels = option.choices
        .filter((choice) => selectedValues.includes(choice.value))
        .map((choice) => choice.label);

      return (
        <Select
          multiple
          displayEmpty
          value={selectedValues}
          onChange={handleMultiSelectChange(option.field)}
          size="small"
          renderValue={() => (selectedLabels.length ? selectedLabels.join(", ") : "Select times")}
          sx={{ minWidth: 180, flexShrink: 0 }}
          MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
        >
          {option.choices.map((choice) => (
            <MenuItem key={choice.value} value={choice.value} dense>
              <Checkbox checked={selectedValues.includes(choice.value)} size="small" />
              <ListItemText primary={choice.label} />
            </MenuItem>
          ))}
        </Select>
      );
    }

    return null;
  };

  const renderSubGroup = (option) => (
    <Paper
      variant="outlined"
      sx={{ bgcolor: "grey.50", borderRadius: 2, mt: 1.5 }}
    >
      <Box px={2} py={1.5}>
        {option.options.map((child, childIndex) => (
          <Box key={child.field}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Box>
                <Typography fontWeight={500}>{child.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                  {child.description}
                </Typography>
              </Box>
              <Box flexShrink={0}>{renderFieldControl(child)}</Box>
            </Box>
            {childIndex < option.options.length - 1 && <Divider sx={{ my: 1.5 }} />}
          </Box>
        ))}
      </Box>
    </Paper>
  );

  const renderOption = (option) => {
    if (option.type === "sub-group") {
      return renderSubGroup(option);
    }

    if (["number", "select", "multi-select"].includes(option.type)) {
      return (
        <Box display="flex" alignItems="flex-start">
          {renderFieldControl(option)}
          <Box ml={2}>
            <Typography fontWeight={500}>{option.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {option.description}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <FormControlLabel
        control={
          <Switch
            checked={!!getFieldValue(option.field)}
            onChange={handleToggle(option.field)}
            color="primary"
            sx={{
              width: 70,
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  transform: "translateX(32px)",
                },
              },
              "& .MuiSwitch-track": {
                width: 52,
              },
            }}
          />
        }
        label={
          <Box ml={option.nested ? 3 : 1}>
            <Typography fontWeight={500}>{option.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {option.description}
            </Typography>
          </Box>
        }
        sx={option.nested ? { ml: 4 } : undefined}
      />
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const variables = getProjectVariables({ ...values, id: projectId });
      const response = await updateProject({
        variables,
      });

      if (response.data?.updateProject?.project) {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const isDirtyFn = useCallback(() => {
    if (!initialValuesRef.current) return false;
    return toggleFields.some((field) => get(values, field) !== initialValuesRef.current[field]);
  }, [toggleFields, values]);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving: unsavedSaving,
  } = useUnsavedChanges({
    onClose,
    isDirtyFn,
    onSave: handleSave,
  });

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => { if (!modeModalOpen) attemptClose(); }}
      maxWidth={false}
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: "650px" } }}
    >
      <DialogTitle sx={{ position: "relative" }}>
        <Typography align="center" variant="h4" color="text.primary">
          {settings.filter_field2} Toggles
        </Typography>
        <IconButton
          onClick={attemptClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Box mb={2}>
            <Typography variant="body1" fontWeight={500} mb={1}>
              Mode
            </Typography>
            <Paper
              elevation={0}
              onClick={(e) => { e.stopPropagation(); setModeModalOpen(true); }}
              sx={{
                border: "2px solid",
                borderColor: "primary.main",
                bgcolor: "rgba(25, 118, 210, 0.06)",
                borderRadius: 2,
                cursor: "pointer",
                position: "relative",
                "&:hover .mode-edit-icon": { opacity: 1 },
              }}
            >
              <Box p={2} display="flex" alignItems="center" gap={1.5}>
                {(() => {
                  const ModeIcon = MODE_ICON_MAP[currentWorkflow] || WarehouseOutlinedIcon;
                  return (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "rgba(25, 118, 210, 0.12)",
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <ModeIcon fontSize="small" />
                    </Box>
                  );
                })()}
                <Box flex={1}>
                  <Typography fontWeight={500} color="text.primary">
                    {currentWorkflowOption?.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentWorkflowOption?.description}
                  </Typography>
                </Box>
                <Box
                  className="mode-edit-icon"
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s",
                    flexShrink: 0,
                    ml: 1,
                    color: "text.secondary",
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </Box>
              </Box>
            </Paper>
          </Box>
          {categories.map((categoryGroup) => (
            <Box key={categoryGroup.category} mb={2}>
              <Typography variant="body1" fontWeight={500} mb={1}>
                {categoryGroup.category}
              </Typography>
              <Paper variant="outlined">
                <Box p={2}>
                  {categoryGroup.options.map((option, optionIndex) => {
                    const isLast = optionIndex === categoryGroup.options.length - 1;
                    const nextIsSubGroup =
                      categoryGroup.options[optionIndex + 1]?.type === "sub-group";

                    return (
                      <Box key={option.field}>
                        {renderOption(option)}
                        {!isLast && !nextIsSubGroup && <Divider sx={{ my: 1.5 }} />}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Box>
          ))}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={attemptClose} variant="outlined" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} />}
        >
          Save
        </Button>
      </DialogActions>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={unsavedSaving}
        />
      )}
    </Dialog>
    <ReceivingWorkflowSelectionModal
      open={modeModalOpen}
      onClose={() => setModeModalOpen(false)}
      currentValue={currentWorkflow}
      onSelect={handleModeSelect}
      cardOptions={availableCardOptions}
    />
    </>
  );
}
